import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import MovieCard from "@/components/feature/MovieCard";
import { useSeo } from "@/hooks/useSeo";
import { channels } from "@/mocks/channels";
import { movies } from "@/mocks/movies";

const channelMovies: Record<string, string[]> = {
  c1: ["1001", "1002", "1003", "1004"],
  c2: ["1007", "1008", "1009", "1010"],
  c3: ["1003", "1015", "1005", "1006"],
  c4: ["1013", "1014", "1009", "1006"],
  c5: ["1011", "1012", "1010", "1004"],
  c6: ["1003", "1006", "1015", "1005"],
};

export default function ChannelDetail() {
  const { id = "" } = useParams();
  const channel = channels.find((c) => c.id === id);

  useSeo({
    title: channel ? `${channel.name} - 黑桃视频影视频道` : "频道不存在 - 黑桃视频",
    description: channel ? channel.intro : "该影视频道不存在，返回黑桃视频频道推荐专题浏览更多频道。",
    keywords: channel
      ? `${channel.name},${channel.tags.join(",")},影视频道,黑桃视频,频道推荐`
      : "影视频道,黑桃视频",
  });

  if (!channel) {
    return (
      <Layout>
        <div className="mx-auto flex w-full max-w-[1440px] flex-col items-center justify-center px-4 py-24 text-center">
          <span className="flex h-14 w-14 items-center justify-center text-foreground-400">
            <i className="ri-tv-2-line text-[48px]" aria-hidden="true"></i>
          </span>
          <h1 className="mt-4 font-heading text-xl font-600 text-foreground-950">频道内容暂时更新中</h1>
          <p className="mt-2 text-sm text-foreground-600">你可能访问了一个暂不存在的影视频道。</p>
          <Link
            to="/channel"
            className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-500 text-background-50 transition-colors hover:bg-primary-600"
          >
            返回频道专题
          </Link>
        </div>
      </Layout>
    );
  }

  const related = (channelMovies[channel.id] || [])
    .map((mid) => movies.find((m) => m.id === mid))
    .filter((m): m is NonNullable<typeof m> => Boolean(m));

  const otherChannels = channels.filter((c) => c.id !== channel.id).slice(0, 3);

  return (
    <Layout>
      <div className="mx-auto w-full max-w-[1440px] px-4 pt-6 md:px-8 md:pt-10">
        <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="transition-colors hover:text-primary-500">首页</Link>
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-arrow-right-s-line text-[14px]" aria-hidden="true"></i>
          </span>
          <Link to="/channel" className="transition-colors hover:text-primary-500">频道推荐</Link>
          <span className="flex h-3.5 w-3.5 items-center justify-center">
            <i className="ri-arrow-right-s-line text-[14px]" aria-hidden="true"></i>
          </span>
          <span className="text-foreground-800">{channel.name}</span>
        </nav>

        <Link
          to="/channel"
          className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-500"
        >
          <i className="ri-arrow-left-line text-[16px]" aria-hidden="true"></i>
          返回频道专题
        </Link>

        <div className="mt-5 overflow-hidden rounded-xl border border-background-200 bg-background-100">
          <div className="relative h-[220px] w-full md:h-[320px]">
            <img
              src={channel.cover}
              alt={`${channel.name} 频道主图`}
              title={`${channel.name} - 黑桃视频影视频道`}
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 to-transparent"></div>
            <div className="absolute bottom-0 left-0 w-full p-5 md:p-7">
              <h1 className="font-heading text-2xl font-700 tracking-wide text-background-950 md:text-3xl">
                {channel.name}
              </h1>
              <p className="mt-1.5 text-sm text-foreground-800">{channel.stats}</p>
            </div>
          </div>
          <div className="p-5 md:p-7">
            <p className="text-sm leading-7 text-foreground-700">{channel.intro}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {channel.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-900">
                  {tag}
                </span>
              ))}
            </div>
            <div className="mt-5 rounded-lg border border-primary-500/30 bg-primary-500/10 p-4">
              <p className="flex items-start gap-2 text-sm text-foreground-800">
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-primary-500">
                  <i className="ri-lightbulb-line text-[18px]" aria-hidden="true"></i>
                </span>
                <span><strong className="font-600 text-foreground-950">推荐理由：</strong>{channel.reason}</span>
              </p>
            </div>
            <p className="mt-4 text-xs text-foreground-500">
              说明：本频道为内容推荐展示，暂不提供视频播放与下载功能，页面内容均为演示数据。
            </p>
          </div>
        </div>

        <section className="mt-12">
          <h2 className="font-heading text-xl font-600 tracking-wide text-foreground-950">频道精选内容</h2>
          <div className="mt-5 grid grid-cols-2 gap-3.5 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="font-heading text-xl font-600 tracking-wide text-foreground-950">其他频道</h2>
          <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-3">
            {otherChannels.map((c) => (
              <Link
                key={c.id}
                to={`/channel/${c.id}`}
                className="group flex items-center gap-3 rounded-lg border border-background-200 bg-background-100 p-3 transition-colors hover:border-accent-400"
              >
                <div className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={c.cover}
                    alt={`${c.name} 频道封面`}
                    title={`${c.name} - 黑桃视频影视频道推荐`}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="min-w-0">
                  <h3 className="truncate text-sm font-500 text-foreground-950 transition-colors group-hover:text-accent-500">
                    {c.name}
                  </h3>
                  <p className="mt-1 truncate text-xs text-foreground-600">{c.short}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}